import {z} from "../../../../../lib";
import {
    OperacionCLFBDetalleFields,
    OperacionCLFBDetalleHistoriaFields,
    OperacionCLFBFields,
    OperacionCLFBLimitesAprobadosFields
} from "./fields";
import {EntityWithId, EntityWithIdFields, schemaEntityWithId} from "../../../../../types";
import {OperacionCLGFields, OperacionCLGLimitesAprobadosPaisFields} from "../../clg/types/fields";
import {CLGLimiteAprobado, schemaCLGLimiteAprobado} from "../../clg/types/types";

export const schemaOperacionCLFB = z
    .object({
        [OperacionCLFBFields.CodPais]: z.number(),
        [OperacionCLFBFields.NombrePais]: z.string(),
        [OperacionCLFBFields.CodOperacion]: z.number(),
        [OperacionCLFBFields.AgregadoAbsoluto]: z.number(),
        [OperacionCLFBFields.AgregadoNeto]: z.number(),
        [OperacionCLFBFields.FechaAprobacion]: z.coerce.date(),
        [OperacionCLFBFields.FechaVencimiento]: z.coerce.date(),
        [OperacionCLFBFields.FechaAlta]: z.coerce.date().nullable(),
        [OperacionCLFBFields.IdUsuarioAlta]: z.number(),
        [OperacionCLFBFields.NombreUsuarioAlta]: z.string(),
        [OperacionCLFBFields.ApellidoUsuarioAlta]: z.string(),
        [OperacionCLFBFields.MailUsuarioAlta]: z.string(),
        [OperacionCLFBFields.IdMensaje]: z.number(),
        [OperacionCLFBFields.PermiteEditar]: z.boolean(),
        [OperacionCLFBFields.PermiteEliminar]: z.boolean(),
        [OperacionCLFBFields.EstadoFinalizado]: z.boolean(),
    })
    .extend({
        [EntityWithIdFields.Id]: z.number()
    })

export type OperacionCLFB = z.infer<typeof schemaOperacionCLFB>

const schemaFormCompartido = {
    [OperacionCLFBFields.FechaAprobacion]: z.date(),
    [OperacionCLFBFields.FechaVencimiento]: z.date(),
    [OperacionCLFBFields.AgregadoAbsoluto]: z.number(),
    [OperacionCLFBFields.AgregadoNeto]: z.number(),
}

export const schemaFormOperacionCLFB = z.object(schemaFormCompartido)
    .refine(
        schema =>
            (schema[OperacionCLFBFields.FechaVencimiento]?.getTime() ?? 0) >=
            schema[OperacionCLFBFields.FechaAprobacion].getTime(),
        {
            message: 'La fecha de vencimiento debe ser mayor a la fecha de emisión.',
            path: [OperacionCLFBFields.FechaVencimiento]
        }
    );

export type FormOperacionCLFB = z.infer<typeof schemaFormOperacionCLFB>

export const CLFBDetalleInsertSchema = z.object({
    [OperacionCLFBDetalleFields.IdOperacion]: z.number(),
    [OperacionCLFBDetalleFields.CodOperacionActivoTipo]: z.number().nullable(),
    [OperacionCLFBDetalleFields.DescOperacionActivoTipo]: z.string().nullable(),
    [OperacionCLFBDetalleFields.CreditLimitAbsoluto]: z.number(),
    [OperacionCLFBDetalleFields.CreditLimitNeto]: z.number(),
    [OperacionCLFBDetalleFields.CreditLimitConcentracionMaxima]: z.number(),
    [OperacionCLFBDetalleFields.StopLossVerde]: z.string(),
    [OperacionCLFBDetalleFields.StopLossAmarilla]: z.string(),
    [OperacionCLFBDetalleFields.StopLossRoja]: z.string(),
    [OperacionCLFBDetalleFields.HoldingPeriodVerde]: z.string(),
    [OperacionCLFBDetalleFields.HoldingPeriodAmarilla]: z.string(),
    [OperacionCLFBDetalleFields.HoldingPeriodRoja]: z.string(),
})

export type CLFBDetalle_InsertDTO = z.infer<typeof CLFBDetalleInsertSchema>

export const schemaFormNuevaOperacionCLFB = z.object({
    ...schemaFormCompartido,
    [OperacionCLFBFields.ListaDetalles]: z.array(
        CLFBDetalleInsertSchema
    ),
}).refine(
    schema =>
        (schema[OperacionCLFBFields.FechaVencimiento]?.getTime() ?? 0) >=
        schema[OperacionCLFBFields.FechaAprobacion].getTime(),
    {
        message: 'La fecha de vencimiento debe ser mayor a la fecha de emisión.',
        path: [OperacionCLFBFields.FechaVencimiento]
    }
);

export type FormNuevaOperacionCLFB = z.infer<typeof schemaFormNuevaOperacionCLFB>



export const schemaCLFBDetalle = z
    .object({
        [OperacionCLFBDetalleFields.IdOperacion]: z.number(),
        [OperacionCLFBDetalleFields.CodOperacionActivoTipo]: z.number().nullable(),
        [OperacionCLFBDetalleFields.DescOperacionActivoTipo]: z.string().nullable(),
        [OperacionCLFBDetalleFields.CreditLimitAbsoluto]: z.number(),
        [OperacionCLFBDetalleFields.CreditLimitNeto]: z.number(),
        [OperacionCLFBDetalleFields.CreditLimitConcentracionMaxima]: z.number(),
        [OperacionCLFBDetalleFields.StopLossVerde]: z.string(),
        [OperacionCLFBDetalleFields.StopLossAmarilla]: z.string(),
        [OperacionCLFBDetalleFields.StopLossRoja]: z.string(),
        [OperacionCLFBDetalleFields.HoldingPeriodVerde]: z.string(),
        [OperacionCLFBDetalleFields.HoldingPeriodAmarilla]: z.string(),
        [OperacionCLFBDetalleFields.HoldingPeriodRoja]: z.string(),
        [OperacionCLFBDetalleFields.PermiteModificar]: z.boolean()
    })
    .merge(schemaEntityWithId)

export type CLFBDetalle = EntityWithId<number> & {
    [OperacionCLFBDetalleFields.IdOperacion]: number;
    [OperacionCLFBDetalleFields.CodOperacionActivoTipo]: number | null;
    [OperacionCLFBDetalleFields.DescOperacionActivoTipo]: string | null;
    [OperacionCLFBDetalleFields.CreditLimitAbsoluto]: number;
    [OperacionCLFBDetalleFields.CreditLimitNeto]: number;
    [OperacionCLFBDetalleFields.CreditLimitConcentracionMaxima]: number;
    [OperacionCLFBDetalleFields.StopLossVerde]: string;
    [OperacionCLFBDetalleFields.StopLossAmarilla]: string;
    [OperacionCLFBDetalleFields.StopLossRoja]: string;
    [OperacionCLFBDetalleFields.HoldingPeriodVerde]: string;
    [OperacionCLFBDetalleFields.HoldingPeriodAmarilla]: string;
    [OperacionCLFBDetalleFields.HoldingPeriodRoja]: string;
    [OperacionCLFBDetalleFields.PermiteModificar]: boolean;
};

export const schemaCLFBLimiteAprobado = schemaCLFBDetalle
    .extend({
        [OperacionCLFBFields.AgregadoAbsoluto]: z.number(),
        [OperacionCLFBFields.AgregadoNeto]: z.number(),
        [OperacionCLFBDetalleFields.PermiteModificar]: z.boolean().default(false),
        [OperacionCLFBFields.IdMensaje]: z.number(),
        [OperacionCLFBLimitesAprobadosFields.LastUpdateUser]: z.string(),
        [OperacionCLFBLimitesAprobadosFields.LastUpdateDate]: z.coerce.date(),
    });

export type CLFBLimiteAprobado = CLFBDetalle & {
    [OperacionCLFBFields.AgregadoAbsoluto]: number,
    [OperacionCLFBFields.AgregadoNeto]: number,
    [OperacionCLFBLimitesAprobadosFields.LastUpdateUser]: string,
    [OperacionCLFBLimitesAprobadosFields.LastUpdateDate]: Date,
    [OperacionCLFBFields.IdMensaje]: number,
}

export type CLFBLimitesAprobadosPais = {
    [OperacionCLFBFields.AgregadoAbsoluto]: number,
    [OperacionCLFBFields.AgregadoNeto]: number,
    [OperacionCLFBLimitesAprobadosFields.LastUpdateUser]: string;
    [OperacionCLFBLimitesAprobadosFields.LastUpdateDate]: Date;
    [OperacionCLFBLimitesAprobadosFields.MessageId]: number;
    [OperacionCLFBLimitesAprobadosFields.CountryCode]: number;
    [OperacionCLFBLimitesAprobadosFields.CountryName]: string
    [OperacionCLFBLimitesAprobadosFields.ApprovalDate]: Date,
    [OperacionCLFBLimitesAprobadosFields.ExpirationDate]: Date,
    [OperacionCLFBLimitesAprobadosFields.LimitList]: CLFBLimiteAprobado[];
}

export const schemaCLFBLimitesAprobados = z.object({
    [OperacionCLFBFields.AgregadoAbsoluto]: z.number(),
    [OperacionCLFBFields.AgregadoNeto]: z.number(),
    [OperacionCLFBLimitesAprobadosFields.LastUpdateUser]: z.string(),
    [OperacionCLFBLimitesAprobadosFields.LastUpdateDate]: z.coerce.date(),
    [OperacionCLFBLimitesAprobadosFields.MessageId]: z.number(),
    [OperacionCLFBLimitesAprobadosFields.CountryCode]: z.number(),
    [OperacionCLFBLimitesAprobadosFields.CountryName]: z.string(),
    [OperacionCLFBLimitesAprobadosFields.ApprovalDate]: z.coerce.date(),
    [OperacionCLFBLimitesAprobadosFields.ExpirationDate]: z.coerce.date(),

    [OperacionCLFBLimitesAprobadosFields.LimitList]: z.array(schemaCLFBLimiteAprobado),
})


export const schemaCLFBDetalleHistoria = schemaCLFBDetalle.extend({
  [OperacionCLFBDetalleHistoriaFields.UserId]: z.number(),
  [OperacionCLFBDetalleHistoriaFields.User]: z.string(),
  [OperacionCLFBDetalleHistoriaFields.LastUpdateUserId]: z.number(),
  [OperacionCLFBDetalleHistoriaFields.LastUpdateUser]: z.string(),
  [OperacionCLFBDetalleHistoriaFields.LastUpdateStage]: z.string(),
  [OperacionCLFBDetalleHistoriaFields.LastUpdateDate]: z.coerce.date(),
  [OperacionCLFBDetalleFields.PermiteModificar]: z.boolean().optional()
});

export type CLFBDetalleHistoria = z.infer<typeof schemaCLFBDetalleHistoria>


export const CuentaProvisionalFields = {
    IdProvisional: 'idProvisional',
    NroCuenta: 'nroCuenta',
    RazonSocial: 'razonSocial',
    CodMoneda: 'codMoneda',
    ImporteConcertacion: 'importe_Concertacion',
    ImporteDPV: 'importe_DPV',
    ImporteFOP: 'importe_FOP',
    BitEnvio: 'bitEnvio'
} as const

export const detalleProvisional = CLFBDetalleInsertSchema.extend({
    [OperacionCLFBDetalleFields.IdProvisional]: z.string()
});

export type DetalleProvisional = z.infer<typeof detalleProvisional>


